import { Crash, TypeCrashSortKey, TypeCrashStatus } from '@trace/types';
import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { ApiContext } from '../Api/Api';
import { URLStateContext } from '../URLState/URLState';
import appendURLSearchParams from '../../utils/appendURLSearchParams';
import getTimeRange from '../../utils/time/getTimeRange';

export type HookArgs = {
  sortKey?: TypeCrashSortKey;
  sortDirection?: 'asc' | 'desc';
  status?: TypeCrashStatus | 'all';
};
type HookPayload = {
  crashes: Crash[];
  isFetched: boolean;
};
export type HookResult = Pick<UseQueryResult, 'isError' | 'isFetching' | 'refetch'> & HookPayload;

const useCrashList = ({ sortDirection, sortKey, status }: HookArgs): HookResult => {
  const { get } = useContext(ApiContext);
  const { demo, duration, dateFrom, dateTo } = useContext(URLStateContext);
  const { dateFrom: start, dateTo: end } = getTimeRange(duration, dateFrom, dateTo);
  const [crashes, setCrashes] = useState<Crash[]>([]);

  const { data, isError, isFetching, refetch, isSuccess } = useQuery<Crash[]>(
    ['crashList', { demo, status, start, end, sortKey, sortDirection }],
    async () =>
      get(
        `/issues?${appendURLSearchParams(
          '',
          ['status', status !== 'all' ? status : undefined],
          ['start', start.toString()],
          ['end', end.toString()],
          ['sort_by', sortKey],
          ['sort_order', sortDirection],
        )}`,
      ),
  );

  /**
   * Use a side-effect to:
   *  - Update the `crashes` state, whenever the crashList query resolves
   */
  useEffect(() => {
    if (data) {
      setCrashes(data);
    }
  }, [data]);

  return {
    crashes,
    isError,
    isFetched: isSuccess,
    isFetching,
    refetch,
  };
};

export default useCrashList;
